<template>
  <v-row justify="center">
    <v-col md="10" lg="8" xl="5">
      <v-card>
        <v-dialog persistent max-width="600" v-model="editSingleScheduleDialog">
          <v-card>
            <v-card-title>
              {{ $t("admin.activities.edit.editSchedule") }}
            </v-card-title>
            <v-card-text>
              <v-select
                :label="$t('admin.activities.edit.orderInputEditTitle')"
                :items="Array.from({ length: data.schedule.length }, (_, i) => i + 1)"
                v-model="scheduleEditIndex"
              >
              </v-select>
              <v-text-field
                :label="$t('admin.activities.edit.scheduleInputEditTitle')"
                :rules="[$v.required(), $v.maxLength(255)]"
                v-model="scheduleEdit.title"
              ></v-text-field>
              <v-text-field
                v-model="scheduleEdit.durationSeconds"
                :rules="[$v.required(), $v.nonNegative(), $v.isNumber(), $v.min(1)]"
                type="number"
                :label="$t('admin.activities.edit.scheduleInputDurSeconds')"
              ></v-text-field>
              <v-switch
                :label="$t('admin.activities.edit.public')"
                class="mr-5"
                dense
                style="width: 100%"
                v-model="scheduleEdit.public"
              ></v-switch>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="editSingleScheduleDialog = false" color="secondary" text>{{
                $t("admin.activities.edit.scheduleEditCancel")
              }}</v-btn>
              <v-btn @click="updateSingleSchedule" color="primary">{{
                $t("admin.activities.edit.scheduleEditSave")
              }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-form @submit.prevent="save" ref="createForm" v-model="valid">
          <v-card-title>
            <h2>{{ data.name || $t("admin.activities.create.title") }}</h2>
          </v-card-title>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-text-subject</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.contentSettings") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-text-field
              v-model="data.name"
              :label="$t('admin.activities.edit.name')"
              :rules="[$v.required(), $v.maxLength(255)]"
              required
            ></v-text-field>
            <v-textarea
              v-model="data.description"
              :label="$t('admin.activities.edit.description')"
              rows="2"
              auto-grow
              required
            ></v-textarea>
            <v-subheader v-if="data.teaserImageUrl" class="pl-0">{{
              $t("admin.activities.edit.teaser.image")
            }}</v-subheader>
            <v-img
              v-if="data.teaserImageUrl"
              :src="data.teaserImageUrl || ''"
              contain
              max-width="300px"
              max-height="200px"
            >
              <v-btn
                @click="removeTeaserImage()"
                class="float-right ma-1"
                color="error"
                x-small
                v-if="data.teaserImageUrl"
                fab
              >
                <v-icon dark>mdi-delete-forever</v-icon></v-btn
              >
            </v-img>
            <v-file-input
              :label="$t('admin.activities.edit.teaser.image')"
              v-show="!data.teaserImageUrl"
              prepend-icon="mdi-image-outline"
              accept="image/*"
              v-model="fileTeaser"
            ></v-file-input>
          </v-card-text>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-youtube</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.videoSettings") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-select
              :label="$t('admin.activities.types.selectTypeTitle')"
              :items="types"
              item-text="text"
              item-value="value"
              v-model="data.type"
              :rules="[$v.required()]"
            >
            </v-select>
            <template v-if="data.type == 'LIVE_STREAM'">
              <v-text-field
                v-model="data.link"
                :rules="[$v.isUrl(), $v.maxLength(255), $v.required()]"
                :label="$t('admin.activities.edit.link')"
                :hint="$t('admin.activities.edit.linkDescription')"
                persistent-hint
              ></v-text-field>
              <v-text-field
                v-model="data.preStartVideo"
                type="number"
                :rules="[$v.nonNegativeOrEmpty()]"
                :label="$t('admin.activities.edit.linkStartBefore')"
                :hint="$t('admin.activities.edit.linkStartBeforeHint')"
                persistent-hint
              ></v-text-field>
            </template>

            <template v-if="data.type == 'WORKSHOP'">
              <v-text-field
                v-model="formattedZoomNumber"
                :rules="[$v.required(), $v.minLength(11), $v.maxLength(13)]"
                :label="$t('admin.activities.edit.zoomMeetingId')"
                required
                persistent-hint
              ></v-text-field>
              <v-text-field
                v-model="data.zoomMeetingCode"
                :rules="[$v.required(), $v.minLength(6), $v.maxLength(6)]"
                :label="$t('admin.activities.edit.zoomMeetingCode')"
                required
                persistent-hint
              ></v-text-field>
              <v-text-field
                v-model="data.preStartVideo"
                type="number"
                :rules="[$v.nonNegativeOrEmpty()]"
                :label="$t('admin.activities.edit.linkStartWorkshopBefore')"
                :hint="$t('admin.activities.edit.linkStartWorkshopBeforeHint')"
                persistent-hint
              ></v-text-field>
            </template>

            <template v-if="data.type == 'EXTERNAL'">
              <v-text-field
                v-model="data.externalLink"
                :rules="[$v.isUrl(), $v.maxLength(255), $v.required()]"
                :label="$t('admin.activities.edit.externalLink')"
                persistent-hint
              ></v-text-field>
              <v-text-field
                v-model="data.preStartVideo"
                type="number"
                :rules="[$v.nonNegativeOrEmpty()]"
                :label="$t('admin.activities.edit.linkStartExternalBefore')"
                :hint="$t('admin.activities.edit.linkStartExternalBeforeHint')"
                persistent-hint
              ></v-text-field>
            </template>
          </v-card-text>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-calendar</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.planning") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="formattedDate"
                      clearable
                      :label="$t('admin.activities.edit.date')"
                      append-icon="mdi-calendar"
                      v-on="on"
                      @click:clear="clearDates()"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="date" no-title scrollable>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="$refs.menu.save(date)">{{
                      $t("admin.activities.edit.calendarOK")
                    }}</v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  ref="menu2"
                  v-model="menu2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="time"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="time"
                      clearable
                      :label="$t('admin.activities.edit.time')"
                      append-icon="mdi-clock"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-if="menu2"
                    v-model="time"
                    format="24hr"
                    full-width
                    @click:minute="$refs.menu2.save(time)"
                  ></v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="data.duration"
                  min="0"
                  :rules="[$v.nonNegative(), $v.required()]"
                  :label="$t('admin.activities.edit.durationMinutes')"
                  type="number"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-toolbar v-if="data.type == 'LIVE_STREAM'" color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-cog-outline</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.features.title") }}</v-toolbar-title>
          </v-toolbar>
          <v-subheader v-if="data.type == 'LIVE_STREAM'">{{
            $t("admin.activities.edit.features.subheader")
          }}</v-subheader>
          <v-card-text v-if="data.type == 'LIVE_STREAM'">
            <h4>{{ $t("admin.activities.edit.features.coaching") }}</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-switch
                  :label="$t('admin.activities.edit.enableVideoFeedback')"
                  v-model="data.enableVideoFeedback"
                ></v-switch>
              </v-col>
            </v-row>
            <h4>{{ $t("admin.activities.edit.features.participants") }}</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="data.displayParticipantCount"
                  :label="$t('admin.activities.edit.displayParticipantCount')"
                ></v-switch>
              </v-col>
            </v-row>
            <h4>{{ $t("admin.activities.edit.features.interactivity") }}</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-switch :label="$t('admin.activities.edit.enableReactions')" v-model="data.enableEmotes"></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  :label="$t('admin.activities.edit.enableVideoChat')"
                  v-model="data.enableVideoChat"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  :label="$t('admin.activities.edit.enableTeamCaptainSpeech')"
                  v-model="data.enableTeamCaptainSpeech"
                ></v-switch>
              </v-col>
            </v-row>
            <h4>{{ $t("admin.activities.edit.features.challenge") }}</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-switch v-model="data.isRunning" :label="$t('admin.activities.edit.enableRunningEvent')"></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  :label="$t('admin.activities.edit.enablePostEventForm')"
                  v-model="data.enablePostEventForm"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch :label="$t('admin.activities.edit.enableDiploma')" v-model="data.enableDiploma"></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-toolbar v-if="data.type == 'LIVE_STREAM'" color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-clock-outline</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.schedule") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text v-if="data.type == 'LIVE_STREAM'">
            <v-list>
              <v-list-item v-if="!data.schedule || data.schedule.length === 0">
                <v-list-item-content>
                  {{ $t("admin.activities.edit.emptySchedule") }}
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-for="(schedule, idx) in data.schedule" v-bind:key="idx">
                <v-list-item-avatar>
                  <v-avatar color="primary">
                    <span class="font-weight-medium" style="color: white">
                      {{ schedule.order + 1 }}
                    </span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ schedule.title }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-content>
                  <v-list-item-subtitle
                    >({{ schedule.durationSeconds }}
                    {{ $t("admin.activities.edit.scheduleSeconds") }})</v-list-item-subtitle
                  >
                </v-list-item-content>
                <v-list-item-action>
                  <ActivitiesEditCustomPopupModal :schedule="schedule" />
                </v-list-item-action>
                <v-list-item-action>
                  <ActivitiesEditSelfieModal :schedule="schedule" />
                </v-list-item-action>
                <v-list-item-action>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        :color="schedule.enableRequestGeolocation ? 'primary' : 'grey lighten-2'"
                        @click="schedule.enableRequestGeolocation = !schedule.enableRequestGeolocation"
                      >
                        <v-icon>mdi-map-marker</v-icon>
                      </v-btn>
                    </template>
                    <span>Request participants to enable geolocation</span>
                  </v-tooltip>
                </v-list-item-action>
                <v-list-item-action>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        :color="schedule.enableDistanceTracking ? 'primary' : 'grey lighten-2'"
                        @click="schedule.enableDistanceTracking = !schedule.enableDistanceTracking"
                      >
                        <v-icon>mdi-run</v-icon>
                      </v-btn>
                    </template>
                    <span>Challenge time. If geolocation enabled their distance is tracked.</span>
                  </v-tooltip>
                </v-list-item-action>
                <v-list-item-action>
                  <ActivitiesEditWallModal :schedule="schedule" />
                </v-list-item-action>
                <v-list-item-action class="ml-5">
                  <v-btn @click="editSingleSchedule(schedule)" icon text>
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-list-item-action>
                  <v-btn icon text color="error" @click="removeSingleSchedule(schedule)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="scheduleModel !== null">
                <v-list-item-content>
                  <v-form ref="scheduleModelForm" v-model="scheduleModelFormValid">
                    <v-row>
                      <v-col cols="6">
                        <v-text-field
                          v-model="scheduleModel.title"
                          :rules="[$v.required(), $v.maxLength(255)]"
                          :label="$t('admin.activities.edit.scheduleInput')"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field
                          v-model="scheduleModel.durationSeconds"
                          type="number"
                          :rules="[$v.required(), $v.nonNegative(), $v.isNumber(), $v.min(1)]"
                          :label="$t('admin.activities.edit.scheduleInputDurSeconds')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-list-item-content>
                <v-list-item-action>
                  <div>
                    <v-btn icon text color="primary" @click="saveSchedule()">
                      <v-icon>mdi-check</v-icon>
                    </v-btn>
                    <v-btn icon text color="error" @click="abortSchedule()">
                      <v-icon>mdi-cancel</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions v-if="data.type == 'LIVE_STREAM'">
            <v-spacer></v-spacer>
            <v-btn v-if="!scheduleModel" color="primary" outlined @click="addSchedule()">
              <v-icon left>mdi-plus</v-icon>
              {{ $t("admin.activities.edit.addSchedule") }}
            </v-btn>
          </v-card-actions>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-alert-outline</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.activities.edit.advancedSettings") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-text-field
              v-model="data.slug"
              :rules="[$v.maxLength(255)]"
              :label="$t('admin.activities.edit.slug.field')"
              :hint="$t('admin.activities.edit.slug.description')"
            ></v-text-field>
            <v-text-field
              v-if="data.type == 'LIVE_STREAM'"
              v-model="data.contextDescription"
              :label="$t('admin.activities.edit.contextDescription')"
              :hint="$t('admin.activities.edit.contextDescriptionHint')"
              :rules="[$v.maxLength(45)]"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit" :disabled="!valid" :loading="loading">
              {{ $t("admin.activities.edit.confirm") }}
            </v-btn>
            <v-btn color="secondary" text @click="cancel()">
              {{ $t("admin.activities.edit.cancel") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import moment from "moment";
import axios from "axios";
import slugify from "slugify";
import ActivitiesEditWallModal from "./activities.edit.wallModal";
import ActivitiesEditSelfieModal from "./activities.edit.selfieModal";
import ActivitiesEditCustomPopupModal from "@/pages/admin/event/activities.edit.customPopupModal";
export default {
  components: {
    ActivitiesEditCustomPopupModal,
    ActivitiesEditSelfieModal,
    ActivitiesEditWallModal,
  },
  props: {},
  data() {
    return {
      data: {
        schedule: [],
        zoomMeetingNumber: null,
      },
      title: "",
      menu: false,
      menu2: false,
      valid: false,
      time: null,
      date: null,
      loading: false,
      fileTeaser: null,
      durationMinutes: null,
      scheduleModel: null,
      scheduleModelFormValid: null,
      messages: [],
      editSingleScheduleDialog: false,
      scheduleEdit: {},
      scheduleEditIndex: null,
      prevScheduleEditIndex: null,
      types: [
        { value: "WORKSHOP", text: this.$t("admin.activities.types.workshop") },
        { value: "LIVE_STREAM", text: this.$t("admin.activities.types.live_stream") },
        { value: "EXTERNAL", text: this.$t("admin.activities.types.external") },
      ],
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      if (this.$route.params.id) {
        this.data = await this.$axios.get(`/event/activities/${this.$route.params.id}`);
        this.title = this.data.name;
        this.time = this.data.date ? moment(this.data.date).format("HH:mm") : null;
        this.date = this.data.date;
      }
    },
    addSchedule() {
      this.scheduleModel = {
        title: "",
        duration: 0,
        order: this.data.schedule ? this.data.schedule.length : 0,
      };
    },
    saveSchedule() {
      this.$refs.scheduleModelForm.validate();
      if (this.scheduleModelFormValid) {
        if (!this.data.schedule) {
          this.data.schedule = [];
        }
        this.data.schedule.push(this.scheduleModel);
        this.abortSchedule();
      }
    },
    abortSchedule() {
      this.scheduleModel = null;
    },
    removeSingleSchedule(item) {
      const idx = this.data.schedule.indexOf(item);
      this.data.schedule.splice(idx, 1);
      this.reorderSchedule();
    },
    editSingleSchedule(item) {
      this.prevScheduleEditIndex = this.data.schedule.indexOf(item) + 1;
      this.scheduleEditIndex = this.data.schedule.indexOf(item) + 1;
      this.scheduleEdit = Object.assign({}, item);
      this.editSingleScheduleDialog = true;
    },

    updateOrder(arr, oldIdx, newIdx) {
      if (newIdx >= arr.length) {
        var k = newIdx - arr.length + 1;
        while (k--) {
          arr.push(undefined);
        }
      }
      arr.splice(newIdx, 0, arr.splice(oldIdx, 1)[0]);
    },

    updateSingleSchedule() {
      this.prevScheduleEditIndex--;
      this.scheduleEditIndex--;

      // Apply changes to edited item
      Object.assign(this.data.schedule[this.prevScheduleEditIndex], this.scheduleEdit);

      // If order was changed
      if (this.prevScheduleEditIndex != this.scheduleEditIndex) {
        // Update array order
        this.updateOrder(this.data.schedule, this.prevScheduleEditIndex, this.scheduleEditIndex);

        // Update order field
        this.data.schedule.map((item, idx) => (item.order = idx));
      }

      // Clean up
      this.scheduleEdit = {};
      this.editSingleScheduleDialog = false;
      this.prevScheduleEditIndex = null;
      this.scheduleEditIndex = null;
    },
    reorderSchedule() {
      if (this.data.schedule) {
        this.data.schedule.sort((a, b) => a.order - b.order);
        this.data.schedule.forEach((item, index) => {
          item.order = index;
        });
      }
    },
    removeTeaserImage() {
      this.data.teaserImageUrl = null;
      this.fileTeaser = null;
    },
    async save() {
      if (!this.$data.valid) return;
      this.loading = true;
      if (this.fileTeaser) {
        const formData = new FormData();
        formData.append("file", this.fileTeaser);
        const axiosResponse = await axios.post("/files", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.data.teaserImageUrl = axiosResponse.serveURL;
      }
      this.data.id_event = this.$route.params.eventId;
      const timeParts = this.time ? this.time.split(":") : "";
      if (this.date) {
        this.data.date = moment(this.date).set({
          hour: parseInt(timeParts[0]),
          minute: parseInt(timeParts[1]),
        });
      } else {
        this.data.date = null;
      }

      if (!/^[0-9]+$/.test(this.data.preStartVideo)) {
        this.data.preStartVideo = null;
      }

      try {
        if (this.$route.params.id) {
          await this.$axios.put(`/admin/activities/${this.$route.params.id}`, this.data);
        } else {
          await this.$axios.post(`/admin/activities/`, this.data);
        }
        this.$store.commit("SUCCESS", this.$t("admin.activities.edit.msgSaved"));
        this.loading = false;
        this.$router.push({ name: "activities" });
      } catch(error) {
        this.loading = false;
      }
      
    },

    cancel() {
      this.$router.push({ name: "activities" });
    },
    clearDates() {
      this.date = null;
      this.time = null;
    },
  },
  watch: {
    "data.name": {
      handler() {
        if (!this.$route.params.id && this.data.name) {
          this.data.slug = slugify(`${this.$route.params.eventId}-${this.data.name.toLowerCase()}`, {
            lower: true,
            strict: true,
          });
        }
      },
      deep: true,
    },
  },
  computed: {
    formattedDate: {
      get() {
        if (!this.date) return null;
        return moment(this.date).format("DD/MM/YYYY");
      },
      set(date) {
        const parsedDate = moment(date, "DD/MM/YYYY");
        if (parsedDate.isValid()) {
          this.date = parsedDate;
        }
      },
    },
    formattedZoomNumber: {
      get() {
        if (!this.data.zoomMeetingNumber) return null;
        else if (this.data.zoomMeetingNumber.length < 3) return this.data.zoomMeetingNumber.slice(0, 3);
        else if (this.data.zoomMeetingNumber.length < 7)
          return this.data.zoomMeetingNumber.slice(0, 3) + " " + this.data.zoomMeetingNumber.slice(3, 7);
        else
          return (
            this.data.zoomMeetingNumber.slice(0, 3) +
            " " +
            this.data.zoomMeetingNumber.slice(3, 7) +
            " " +
            this.data.zoomMeetingNumber.slice(7, 11)
          );
      },
      set(number) {
        const joinedNumber = number.replace(/\s/g, "");
        this.data.zoomMeetingNumber = joinedNumber;
      },
    },
  },
};
</script>
